import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import type { ColumnDef } from '@tanstack/react-table';

import { Can } from '@/components/can/Can';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { Button } from '@/design-system/button';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useDynamicLocale } from '@/hoslog-intl/Translation';
import { LockCloseIcon, LockOpenIcon } from '@/icons';
import { commaSeparator } from '@/lib/commaSeparator';
import { formatDate } from '@/lib/dateFormatter';
import { ResetPasswordPayload, ToggleStatusPayload } from '@/services/customers/customers.mutation';
import { CUSTOMER_STATUS, Customers } from '@/services/customers/customers.types';
import { Link } from '@tanstack/react-router';
import { Edit, KeyRoundIcon, Trash2 } from 'lucide-react';

type ModalSize = 'sm' | 'md' | 'lg';
type ModalVariant = 'default' | 'destructive' | 'warning';

type OpenConfirmationProps = (data: {
  title: string;
  description: string;
  cancelLabel: string;
  actionLabel: string;
  content?: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  onAction: () => Promise<void>;
  onCancel: () => void;
}) => void;

export function getColumns(
  openConfirmation: OpenConfirmationProps,
  mutateAsync: (payload: ToggleStatusPayload) => Promise<unknown>,
  resetPassword: (payload: ResetPasswordPayload) => Promise<unknown>,
  deleteCustomer: (customerId: string) => Promise<unknown>,
  t: ReturnType<typeof useDynamicLocale>['t'],
): ColumnDef<Customers>[] {
  return [
    {
      accessorKey: 'id',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => (
        <div className="font-medium text-caption text-primary truncate max-w-[10rem]">
          {getSerialNumber(table, row.index)}
        </div>
      ),
      enableHiding: false,
      enableSorting: false,
    },
    {
      accessorKey: 'customerInfo',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.customerInfo')} />
      ),
      cell: ({ row }) => (
        <div className="font-medium text-caption text-primary truncate max-w-[20rem]">
          {row.getValue('customerInfo') ? row.getValue('customerInfo') : '-'}
        </div>
      ),
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.customerInfo',
      },
      enableHiding: true,
    },
    {
      accessorKey: 'emailAddress',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.emailAddress')} />
      ),
      cell: ({ row }) => {
        const email: string = row.getValue('emailAddress');
        return (
          <a href={`mailto:${email}`} className="text-link font-medium text-caption underline">
            {email}
          </a>
        );
      },

      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.emailAddress',
      },
      enableHiding: true,
    },
    {
      accessorKey: 'mobileNumber',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.phoneNumber')} />
      ),
      cell: ({ row }) => {
        return <div className="font-medium text-caption text-primary">{row.getValue('mobileNumber')}</div>;
      },
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.phoneNumber',
      },
      enableHiding: true,
    },
    {
      accessorKey: 'rewardPoints',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.rewardPoints')} />
      ),
      cell: ({ row }) => (
        <div className="text-right font-medium text-caption text-primary">
          {commaSeparator(Math.abs(row.getValue('rewardPoints')))} 円
        </div>
      ),
      meta: { align: 'right', hideColumnLabelKey: 'common.page.tableColumn.rewardPoints' },
      enableHiding: true,
    },
    {
      accessorKey: 'referrer',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.referrer')} />,
      cell: ({ row }) => (
        <div className="font-medium text-caption text-primary">
          {row.getValue('referrer') ? row.getValue('referrer') : '-'}
        </div>
      ),
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.referrer',
      },
      enableHiding: true,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.status')} />,
      cell: ({ cell }) => {
        const status = cell.getValue<Customers['status']>();
        return (
          <div>
            {status === CUSTOMER_STATUS.ACTIVE ? (
              <div className="text-success font-medium text-caption">{t('common.page.table.active')}</div>
            ) : (
              <div className="text-destructive-text font-medium text-caption">{t('common.page.table.blocked')}</div>
            )}
          </div>
        );
      },
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.status',
      },
      enableHiding: true,
      enableColumnFilter: false,
    },
    {
      id: 'createdDate',
      accessorKey: 'createdDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.createdDate')} />
      ),
      cell: ({ row }) => (
        <div className="text-caption font-medium whitespace-nowrap">
          {formatDate(Number(row.getValue('createdDate')), 'YYYY/MM/DD')}
        </div>
      ),
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.createdDate',
      },
      enableHiding: true,
      enableColumnFilter: true,
    },
    {
      accessorKey: 'updatedDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.updatedDate')} />
      ),
      cell: ({ row }) => (
        <div className="text-caption font-medium whitespace-nowrap">
          {formatDate(Number(row.getValue('updatedDate')), 'YYYY/MM/DD')}
        </div>
      ),
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.updatedDate',
      },
      enableHiding: true,
    },
    {
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.action')} />,
      id: 'actions',
      cell: ({ row }) => {
        const name = row.original.customerInfo;
        return (
          <div className="flex items-center gap-4">
            {row.original.status === CUSTOMER_STATUS.ACTIVE && (
              <>
                <Can rule={{ anyOf: [PERMS.customer.detailView] }}>
                  <Link
                    to="/users/customers/$customerId"
                    params={{ customerId: row.original.agentId }}
                    search={{ breadcrumbReturnTo: window.location.href }}
                  >
                    <Edit size={16} className="cursor-pointer text-foreground-caption hover:text-foreground-detail" />
                  </Link>
                </Can>
                <Can rule={{ anyOf: [PERMS.customer.passwordReset] }}>
                  <KeyRoundIcon
                    size={16}
                    className="cursor-pointer text-warning hover:text-warning"
                    onClick={() => {
                      openConfirmation({
                        title: t('common.page.modal.confirmation.title'),
                        description: t('userManagement.affiliate.resetPasswordModel.message', { affiliateName: name }),
                        cancelLabel: t('common.page.modal.no'),
                        actionLabel: t('common.page.modal.confirm'),
                        onAction: async () => {
                          await resetPassword({
                            customerId: row.original.agentId,
                          });
                        },
                        onCancel: () => {},
                        size: 'lg',
                        variant: 'warning',
                      });
                    }}
                  />
                </Can>
              </>
            )}
            <Can rule={{ anyOf: [PERMS.customer.statusUpdate] }}>
              <Button
                variant={'ghost'}
                className="p-0 m-0"
                onClick={() => {
                  openConfirmation({
                    title: t('common.page.modal.confirmation.title'),
                    description:
                      row.getValue('status') === 'A'
                        ? t('userManagement.affiliate.blockUnblockModel.blockMessage', { affiliateName: name })
                        : t('userManagement.affiliate.blockUnblockModel.unblockMessage', { affiliateName: name }),
                    cancelLabel: t('common.page.modal.no'),
                    actionLabel: t('common.page.modal.confirm'),
                    onAction: async () => {
                      await mutateAsync({
                        customerId: row.original.agentId,
                        status: row.getValue('status') === 'A' ? 'B' : 'A',
                      });
                    },
                    onCancel: () => {},
                    size: 'lg',
                    variant: 'warning',
                  });
                }}
              >
                {row.getValue('status') === 'A' ? (
                  <LockCloseIcon className="text-destructive-text size-4" />
                ) : (
                  <LockOpenIcon className="text-link size-4" />
                )}
              </Button>
            </Can>
            {row.original.status === CUSTOMER_STATUS.ACTIVE && (
              <Can rule={{ anyOf: [PERMS.customer.manage] }}>
                <Trash2
                  size={16}
                  className="cursor-pointer text-destructive-text hover:text-destructive-text"
                  onClick={() => {
                    openConfirmation({
                      title: t('common.page.modal.confirmation.title'),
                      description: t('storeManagement.basicStores.confirmationModal.deleteItem'),
                      cancelLabel: t('common.page.modal.no'),
                      actionLabel: t('common.page.modal.confirm'),
                      onAction: async () => {
                        await deleteCustomer(row.original.agentId);
                      },
                      onCancel: () => {},
                      size: 'lg',
                      variant: 'destructive',
                    });
                  }}
                />
              </Can>
            )}
          </div>
        );
      },
      size: 40,
    },
  ];
}
